import { LaunchChangeKind, LaunchEmailRecipient } from './launchChange';

import type { LaunchChange, LaunchEmailRecipientValue } from './launchChange';
import type {
    LaunchAudienceSnapshot,
    LaunchConfigSnapshot,
    LaunchEmailSnapshot,
    LaunchStepSnapshot,
} from './launchConfigSnapshot';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';

import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';

const EVALUATION_TYPE_ORDER: EvaluationTypeValue[] = [
    EvaluationType.MANAGER_EVALUATION,
    EvaluationType.EVALUATION_360,
    EvaluationType.SELF_EVALUATION,
];

const EMAIL_RECIPIENT_ORDER: LaunchEmailRecipientValue[] = [
    LaunchEmailRecipient.EMPLOYEE,
    LaunchEmailRecipient.MANAGER,
];

const areAudienceCriteriaEqual = (
    before: LaunchAudienceSnapshot,
    after: LaunchAudienceSnapshot,
): boolean => {
    const normalize = (labels: string[]) => [...labels].sort().join('|');

    return normalize(before.criteriaLabels) === normalize(after.criteriaLabels);
};

const isSameEmailContent = (before: LaunchEmailSnapshot, after: LaunchEmailSnapshot): boolean =>
    JSON.stringify([before.titleByLocale, before.bodyByLocale]) ===
    JSON.stringify([after.titleByLocale, after.bodyByLocale]);

const readEmail = (
    step: LaunchStepSnapshot,
    recipient: LaunchEmailRecipientValue,
): LaunchEmailSnapshot | undefined =>
    recipient === LaunchEmailRecipient.EMPLOYEE ? step.employeeEmail : step.managerEmail;

const diffName = (before: LaunchConfigSnapshot, after: LaunchConfigSnapshot): LaunchChange[] => {
    if (before.name === after.name) {
        return [];
    }

    return [{ kind: LaunchChangeKind.NAME_CHANGED, from: before.name, to: after.name }];
};

const diffCycleDates = (
    before: LaunchConfigSnapshot,
    after: LaunchConfigSnapshot,
): LaunchChange[] => {
    const changes: LaunchChange[] = [];

    if (before.cycleStart !== after.cycleStart) {
        changes.push({
            kind: LaunchChangeKind.CYCLE_START_CHANGED,
            from: before.cycleStart,
            to: after.cycleStart,
        });
    }

    if (before.cycleEnd !== after.cycleEnd) {
        changes.push({
            kind: LaunchChangeKind.CYCLE_END_CHANGED,
            from: before.cycleEnd,
            to: after.cycleEnd,
        });
    }

    return changes;
};

const diffAudience = (
    before: LaunchConfigSnapshot,
    after: LaunchConfigSnapshot,
): LaunchChange[] => {
    if (before.audience.isEntireCompany !== after.audience.isEntireCompany) {
        return [
            {
                kind: LaunchChangeKind.AUDIENCE_CHANGED,
                wasEntireCompany: before.audience.isEntireCompany,
                isEntireCompany: after.audience.isEntireCompany,
            },
        ];
    }

    if (
        after.audience.isEntireCompany ||
        areAudienceCriteriaEqual(before.audience, after.audience)
    ) {
        return [];
    }

    return [{ kind: LaunchChangeKind.AUDIENCE_FILTERS_CHANGED }];
};

const diffEvaluation = ({
    evaluationType,
    before,
    after,
}: {
    evaluationType: EvaluationTypeValue;
    before: LaunchConfigSnapshot;
    after: LaunchConfigSnapshot;
}): LaunchChange[] => {
    const previousEvaluation = before.evaluationsByType[evaluationType];
    const nextEvaluation = after.evaluationsByType[evaluationType];
    const labelKey = nextEvaluation?.labelKey ?? previousEvaluation?.labelKey;

    if (!labelKey) {
        return [];
    }

    const selectedBefore = previousEvaluation?.isSelected ? previousEvaluation : undefined;
    const selectedAfter = nextEvaluation?.isSelected ? nextEvaluation : undefined;

    if (!selectedBefore && selectedAfter) {
        const { scheduledAt } = selectedAfter;

        if (!scheduledAt) {
            return [{ kind: LaunchChangeKind.EVALUATION_ADDED_NO_DATE, evaluationType, labelKey }];
        }

        return [{ kind: LaunchChangeKind.EVALUATION_ADDED, evaluationType, labelKey, scheduledAt }];
    }

    if (selectedBefore && !selectedAfter) {
        return [{ kind: LaunchChangeKind.EVALUATION_REMOVED, evaluationType, labelKey }];
    }

    if (!selectedBefore || !selectedAfter) {
        return [];
    }

    if (selectedBefore.templateId === selectedAfter.templateId) {
        return [];
    }

    const from = selectedBefore.templateLabel;
    const to = selectedAfter.templateLabel;

    if (!selectedBefore.templateId) {
        return [{ kind: LaunchChangeKind.TEMPLATE_SET, evaluationType, labelKey, from, to }];
    }

    return [{ kind: LaunchChangeKind.TEMPLATE_CHANGED, evaluationType, labelKey, from, to }];
};

const diffStepEmails = ({
    previousStep,
    nextStep,
    isAlreadyTriggered,
}: {
    previousStep: LaunchStepSnapshot;
    nextStep: LaunchStepSnapshot;
    isAlreadyTriggered: boolean;
}): LaunchChange[] =>
    EMAIL_RECIPIENT_ORDER.flatMap((recipient): LaunchChange[] => {
        const previousEmail = readEmail(previousStep, recipient);
        const nextEmail = readEmail(nextStep, recipient);

        if (!previousEmail || !nextEmail) {
            return [];
        }

        const base = {
            stepId: nextStep.id,
            labelKey: nextStep.labelKey,
            recipient,
            isAlreadyTriggered,
        };

        if (previousEmail.isEnabled && !nextEmail.isEnabled) {
            return [{ ...base, kind: LaunchChangeKind.EMAIL_DISABLED }];
        }

        if (!previousEmail.isEnabled && nextEmail.isEnabled) {
            return [{ ...base, kind: LaunchChangeKind.EMAIL_ENABLED }];
        }

        if (!nextEmail.isEnabled || isSameEmailContent(previousEmail, nextEmail)) {
            return [];
        }

        return [{ ...base, kind: LaunchChangeKind.EMAIL_CONTENT_CHANGED }];
    });

const diffStep = ({
    previousStep,
    nextStep,
}: {
    previousStep: LaunchStepSnapshot;
    nextStep: LaunchStepSnapshot;
}): LaunchChange[] => {
    const isAlreadyTriggered = !!nextStep.processedAt;
    const changes: LaunchChange[] = [];

    if (previousStep.scheduledAt !== nextStep.scheduledAt) {
        changes.push({
            kind: LaunchChangeKind.STEP_DATE_CHANGED,
            stepId: nextStep.id,
            labelKey: nextStep.labelKey,
            from: previousStep.scheduledAt,
            to: nextStep.scheduledAt,
            isAlreadyTriggered,
        });
    }

    return [...changes, ...diffStepEmails({ previousStep, nextStep, isAlreadyTriggered })];
};

/**
 * A step absent from the baseline is skipped: its evaluation was just added, and
 * `evaluationAdded` already says so — listing its every field again would read as a second change.
 */
const diffSteps = (before: LaunchConfigSnapshot, after: LaunchConfigSnapshot): LaunchChange[] => {
    const previousStepById = new Map(before.steps.map((step) => [step.id, step]));

    return after.steps.flatMap((nextStep) => {
        const previousStep = previousStepById.get(nextStep.id);

        return previousStep ? diffStep({ previousStep, nextStep }) : [];
    });
};

const diffLaunchConfigs = (
    before: LaunchConfigSnapshot,
    after: LaunchConfigSnapshot,
): LaunchChange[] => [
    ...diffName(before, after),
    ...diffCycleDates(before, after),
    ...diffAudience(before, after),
    ...EVALUATION_TYPE_ORDER.flatMap((evaluationType) =>
        diffEvaluation({ evaluationType, before, after }),
    ),
    ...diffSteps(before, after),
];

export { diffLaunchConfigs, EVALUATION_TYPE_ORDER };
